import { useState } from 'react';
import api from '../../services/api';
import { Goal } from './types';

interface GoalFormData {
  name: string;
  description: string;
  dueDate: string;
  categoryId: number | null;
  parentGoalId: number | null;
  isPriority: boolean;
}

export const useGoalOperations = (loadData: () => Promise<any>) => {
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [editingGoalId, setEditingGoalId] = useState<number | null>(null);
  const [filterGoalStatus, setFilterGoalStatus] = useState<'all' | 'active' | 'completed'>('active');
  
  const [goalForm, setGoalForm] = useState<GoalFormData>({
    name: '',
    description: '',
    dueDate: '',
    categoryId: null,
    parentGoalId: null,
    isPriority: false
  });

  const handleCreateGoal = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const goalData = {
        ...goalForm,
        dueDate: goalForm.dueDate || null,
        categoryId: goalForm.categoryId || null,
        parentGoalId: goalForm.parentGoalId || null
      };
      
      if (editingGoalId) {
        await api.put(`/api/goals/${editingGoalId}`, goalData);
        setEditingGoalId(null);
        setGoalForm({ name: '', description: '', dueDate: '', categoryId: null, parentGoalId: null, isPriority: false });
      } else {
        await api.post('/api/goals', goalData);
        setGoalForm({ name: '', description: '', dueDate: '', categoryId: null, parentGoalId: null, isPriority: false });
        setShowGoalForm(false);
      }
      
      await loadData();
    } catch (error) {
      console.error('Failed to save goal:', error);
      alert('Ошибка при сохранении цели');
    }
  };

  const handleEditGoal = (goal: Goal) => {
    setEditingGoalId(goal.id);
    setGoalForm({
      name: goal.name,
      description: goal.description || '',
      dueDate: goal.dueDate || '',
      categoryId: goal.categoryId || null,
      parentGoalId: goal.parentGoalId || null,
      isPriority: goal.isPriority || false
    });
  };

  const handleCancelGoalEdit = () => {
    setEditingGoalId(null);
    setGoalForm({ name: '', description: '', dueDate: '', categoryId: null, parentGoalId: null, isPriority: false });
  };

  const handleDeleteGoal = async (goalId: number) => {
    if (window.confirm('Удалить эту цель?')) {
      try {
        await api.delete(`/api/goals/${goalId}`);
        await loadData();
      } catch (error) {
        console.error('Failed to delete goal:', error);
        alert('Ошибка при удалении цели');
      }
    }
  };

  const handleCompleteGoal = async (goalId: number, goalName: string) => {
    if (window.confirm(`Завершить цель "${goalName}"?`)) {
      try {
        await api.put(`/api/goals/${goalId}`, { status: 'completed' });
        await loadData();
        alert('🎉 Цель завершена!');
      } catch (error) {
        console.error('Failed to complete goal:', error);
        alert('Ошибка при завершении цели');
      }
    }
  };

  const handleActivateGoal = async (goalId: number) => {
    try {
      await api.put(`/api/goals/${goalId}`, { status: 'active' });
      await loadData();
    } catch (error) {
      console.error('Failed to activate goal:', error);
    }
  };

  const handleReorderGoals = async (goalIds: number[]) => {
    try {
      await api.post('/api/goals/reorder', { goalIds });
      // Не перезагружаем данные - порядок уже изменен локально через drag-and-drop
    } catch (error) {
      console.error('Failed to reorder goals:', error);
      // При ошибке перезагружаем данные чтобы вернуть старый порядок
      await loadData();
    }
  };

  return {
    showGoalForm,
    setShowGoalForm,
    editingGoalId,
    setEditingGoalId,
    filterGoalStatus,
    setFilterGoalStatus,
    goalForm,
    setGoalForm,
    handleCreateGoal,
    handleEditGoal,
    handleCancelGoalEdit,
    handleDeleteGoal,
    handleCompleteGoal,
    handleActivateGoal,
    handleReorderGoals
  };
};
